import { useId, useState } from 'react';
import type { TodayBreakdownResponse } from '@bazaar/shared';
import { EmptyState } from '../EmptyState.js';
import { Legend, type LegendItem } from './Legend.js';
import { buildPieSlices } from './colors.js';

export interface PieChartProps {
  totals: TodayBreakdownResponse['totals'];
  totalQty: number;
  groupByLabel: string;
}

const SIZE = 220;
const CENTER = SIZE / 2;
const OUTER_R = 96;
const INNER_R = 58; // donut hole - makes room for the centre total

function polarToCartesian(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) };
}

/** An SVG path for one donut slice between two angles (degrees, clockwise from 12 o'clock).
 * A true 360deg sweep (a single slice covering 100% of the total) is capped
 * just short of a full circle - an SVG arc can't represent one at all (the
 * start and end points coincide, collapsing to a zero-area path, which is
 * why a lone 100% category used to render as an invisible sliver). The 0.01deg
 * gap this leaves is imperceptible. */
function donutSlicePath(startAngle: number, endAngleRaw: number): string {
  const endAngle = Math.min(endAngleRaw, startAngle + 359.99);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const startOuter = polarToCartesian(startAngle, OUTER_R);
  const endOuter = polarToCartesian(endAngle, OUTER_R);
  const startInner = polarToCartesian(endAngle, INNER_R);
  const endInner = polarToCartesian(startAngle, INNER_R);
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${OUTER_R} ${OUTER_R} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${INNER_R} ${INNER_R} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
}

/**
 * Donut chart: total quantities sold today, split by category/product.
 * Segments ordered largest first (clockwise from 12 o'clock); slices below
 * ~3% of the total, or past the 4th distinct colour, fold into "Other" -
 * see `buildPieSlices` and the palette comment in theme/tokens.css for why.
 */
export function PieChart({ totals, totalQty, groupByLabel }: PieChartProps) {
  const titleId = useId();
  const [active, setActive] = useState<{ label: string; qty: number; pct: number } | null>(null);

  if (totalQty <= 0 || totals.length === 0) {
    return (
      <EmptyState
        title="No sales yet today"
        description="Today's totals by category or product will show up here."
      />
    );
  }

  const slices = buildPieSlices(totals);

  let cursor = 0;
  const arcs = slices.map((slice) => {
    const sweep = (slice.qty / totalQty) * 360;
    // A 2px surface-colour gap between slices, same spirit as the bar
    // chart's stacked-segment gap - drawn as a stroke rather than shrinking
    // the arc, so very thin slices stay visible.
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    return { ...slice, start, end };
  });

  const ariaLabel = `Today's sales by ${groupByLabel}: ${arcs
    .map((a) => `${a.label} ${a.pct}%`)
    .join(', ')}. ${totalQty} items total.`;

  return (
    <div>
      <div
        role="img"
        aria-labelledby={titleId}
        className="flex flex-col items-center gap-3 sm:flex-row sm:items-start"
      >
        <span id={titleId} className="sr-only">
          {ariaLabel}
        </span>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="presentation"
          className="shrink-0"
        >
          {arcs.map((arc) => (
            <path
              key={arc.key}
              d={donutSlicePath(arc.start, arc.end)}
              fill={arc.color}
              stroke="var(--surface)"
              strokeWidth={2}
              tabIndex={0}
              role="button"
              aria-label={`${arc.label}: ${arc.qty} items, ${arc.pct}%`}
              onMouseEnter={() => setActive({ label: arc.label, qty: arc.qty, pct: arc.pct })}
              onFocus={() => setActive({ label: arc.label, qty: arc.qty, pct: arc.pct })}
              onClick={() => setActive({ label: arc.label, qty: arc.qty, pct: arc.pct })}
            >
              <title>{`${arc.label}: ${arc.qty} items, ${arc.pct}%`}</title>
            </path>
          ))}
          <text
            x={CENTER}
            y={CENTER - 6}
            textAnchor="middle"
            className="font-heading font-semibold"
            style={{ fontSize: 28, fill: 'var(--ink)' }}
          >
            {totalQty}
          </text>
          <text
            x={CENTER}
            y={CENTER + 16}
            textAnchor="middle"
            className="font-body"
            style={{ fontSize: 12, fill: 'var(--muted)' }}
          >
            items today
          </text>
        </svg>

        <div className="min-w-0 w-full flex-1">
          <p className="min-h-5 font-body text-sm text-ink mb-1" role="status">
            {active ? `${active.label}: ${active.qty} (${active.pct}%)` : ' '}
          </p>
          <Legend
            items={slices.map<LegendItem>((s) => ({
              key: s.key,
              label: s.label,
              qty: s.qty,
              color: s.color,
              pct: s.pct,
            }))}
          />
        </div>
      </div>

      {/* `sr-only` on the wrapping div, not the table itself - see the same
          note in BarChart.tsx. */}
      <div className="sr-only">
        <table>
          <caption>Today&rsquo;s sales by {groupByLabel}</caption>
          <thead>
            <tr>
              <th scope="col">{groupByLabel === 'product' ? 'Product' : 'Category'}</th>
              <th scope="col">Quantity</th>
              <th scope="col">Percent</th>
            </tr>
          </thead>
          <tbody>
            {slices.map((s) => (
              <tr key={s.key}>
                <td>{s.label}</td>
                <td>{s.qty}</td>
                <td>{s.pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span data-testid="pie-slice-count" hidden>
        {slices.length}
      </span>
    </div>
  );
}
